import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { opTime } from 'utils/dates';
import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import {
  setSelectedVisit,
  setSelectedVisitor,
} from '../../../../../store/slices/visitsSlice';
import { AppDispatch } from '../../../../../store/store';
import { VisitorSearch } from '../../../../../types/visitorSearchTypes';
import { Visit } from '../../../../../types/visitTypes';
import { formatFullName } from '../../../../../utils/utils';
import {
  getStatusColor,
  profileIcon,
} from '../../../../../utils/visitorsHelper';
import { useTimezoneConverter } from '../../../../../hooks/useTimezoneConverter';

interface VisitorOption {
  label: string;
  value: string;
  visitId: number;
  visitorId: number;
  firstName: string | null;
  lastName: string | null;
  timestamp: string | null;
  visit?: Record<string, never>;
  style?: { color: string };
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[] | null;
}

interface DescribeVisitResponse {
  data?: Visit[] | null;
}

interface SignInModalProps {
  open: boolean;
  locationIdRef: number;
  onClose: () => void;
  openVisitorsDrawer: () => void;
  openSignInManuallyDrawer: () => void;
  visitorNameFilter?: {
    firstName: string;
    middleName: string;
    lastName: string;
  };
}

const SignInModal = ({
  open,
  locationIdRef,
  onClose,
  openVisitorsDrawer,
  openSignInManuallyDrawer,
  visitorNameFilter,
}: SignInModalProps) => {
  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { timezone: locationTz } = useTimezoneConverter(locationIdRef);

  const [visitorOptions, setVisitorOptions] = useState<VisitorOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  // Fetch visitor options
  useEffect(() => {
    const fetchVisitorOptions = async () => {
      setLoading(true);
      try {
        const tz = locationTz || 'UTC';
        const todayStart = opTime()
          .tz(tz)
          .startOf('day')
          .utc()
          .format(DATE_TIME_FORMAT);
        const todayEnd = opTime()
          .tz(tz)
          .endOf('day')
          .utc()
          .format(DATE_TIME_FORMAT);

        const visitors = (await heliumReceive('getSearchVisitors', [orgId], {
          queryStringParams: {
            locationId: locationIdRef,
            startDate: todayStart,
            endDate: todayEnd,
          },
        })) as unknown as SearchVisitorsResponse;

        const filteredPendingVisitors: VisitorOption[] = (visitors.data ?? [])
          .filter(
            (visitor: VisitorSearch) => visitor.status === STATUS.PENDING.id,
          )
          .filter(
            (visitor: VisitorSearch) =>
              hasAllVisitorsWrite || visitor.hostUserId === globalUserId,
          )
          .filter((visitor: VisitorSearch) => {
            // Apply visitor name filter if provided
            if (!visitorNameFilter) return true;

            const firstNameMatch =
              visitorNameFilter.firstName === 'NONE' ||
              visitor.firstName?.toLowerCase() ===
                visitorNameFilter.firstName.toLowerCase();
            const lastNameMatch =
              visitorNameFilter.lastName === 'NONE' ||
              visitor.lastName?.toLowerCase() ===
                visitorNameFilter.lastName.toLowerCase();

            return firstNameMatch && lastNameMatch;
          })
          .map((visitor: VisitorSearch) => ({
            label: formatFullName(
              visitor.firstName,
              visitor.middleName,
              visitor.lastName,
            ),
            value: `${visitor.visitId}-${visitor.id}`,
            visitId: visitor.visitId,
            visitorId: visitor.id,
            firstName: visitor.firstName,
            lastName: visitor.lastName,
            timestamp: visitor.timestamp,
          }));

        // If visitorNameFilter is not provided, add the "Add New Visitor" option
        if (!visitorNameFilter && filteredPendingVisitors.length > 0) {
          filteredPendingVisitors.push({
            label: t('signInModal.addVisitorSpace'),
            value: 'add-new-visitor',
            visitId: -1,
            visitorId: -1,
            firstName: null,
            lastName: null,
            timestamp: null,
            visit: {},
            style: {
              color: 'var(--colorPrimary)',
            },
          });
        }

        setVisitorOptions(filteredPendingVisitors);
      } catch {
        // Failed to fetch visitor options
      } finally {
        setLoading(false);
      }
    };
    fetchVisitorOptions();
  }, [
    orgId,
    locationIdRef,
    visitorNameFilter,
    locationTz,
    t,
    hasAllVisitorsWrite,
    globalUserId,
  ]);

  const handleOptionSelect = async (value: string, option: VisitorOption) => {
    if (value === 'add-new-visitor') {
      openSignInManuallyDrawer();
    } else {
      const selectedVisit = (await heliumReceive('describeVisit', [
        orgId,
        option.visitId,
      ])) as unknown as DescribeVisitResponse;
      if (selectedVisit.data?.[0]) {
        const visit = selectedVisit.data[0];
        const selectedVisitor = visit.visitors.find(
          (v) => Number(v.id) === Number(option.visitorId),
        );
        dispatch(setSelectedVisit(visit));
        dispatch(setSelectedVisitor(selectedVisitor || null));
      }
      openVisitorsDrawer();
    }
    onClose();
  };

  return (
    <OpModal
      title={t('signInModal.title')}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={720}
    >
      <OpSpace
        orientation="vertical"
        size="large"
        style={{ display: 'flex', paddingTop: '10px' }}
      >
        {visitorNameFilter && visitorOptions.length > 1 ? (
          <OpList
            itemLayout="horizontal"
            dataSource={visitorOptions}
            renderItem={(item) => {
              const visitorOption = item as VisitorOption;
              return (
                <OpList.Item
                  onClick={() =>
                    handleOptionSelect(visitorOption.value, visitorOption)
                  }
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                >
                  <OpList.Item.Meta
                    avatar={profileIcon({
                      firstName: visitorOption.firstName || '',
                      lastName: visitorOption.lastName || '',
                      size: 40,
                      color: getStatusColor('Pending'),
                    })}
                    title={visitorOption.label}
                    description={opTime(visitorOption.timestamp).format(
                      DATE_TIME_AM_PM_FORMAT,
                    )}
                  />
                </OpList.Item>
              );
            }}
            style={{ overflowY: 'auto', maxHeight: '50vh' }}
          />
        ) : (
          <OpSelect
            testId="sign-in-visitor-select"
            options={visitorOptions}
            placeholder={t('signInModal.placeholder')}
            onChange={(value, option) =>
              handleOptionSelect(value as string, option as VisitorOption)
            }
            showSearch
            filterOption={(input, option) =>
              String(option?.label)
                ?.toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            loading={loading}
            style={{ width: '100%' }}
            notFoundContent={
              <OpEmpty description={t('signInModal.noVisitor')}>
                <OpButton
                  type="primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    openSignInManuallyDrawer();
                    onClose();
                  }}
                >
                  {t('signInModal.addVisitor')}
                </OpButton>
              </OpEmpty>
            }
          />
        )}
      </OpSpace>
    </OpModal>
  );
};

export default SignInModal;
